import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import { Modal as MuiModal, prepareForSlot } from '@mui/base';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef, useMemo } from 'react';

import { su, theme } from '@adflow/frontend/ui-theme';
import { useWindowViewHeight } from '@adflow/frontend/util-common';

export const styles = {
	modal: css({
		position: 'fixed',
		inset: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	}),
	modalDialogOverride: css({
		padding: '1.25rem',
		backgroundColor: theme.colors.background.form,
		boxShadow: '0px 4px 16px 0px gray',
		borderRadius: '4px',
		overflowY: 'auto',
		boxSizing: 'border-box',
		animation:
			'0.3s cubic-bezier(0, 0, 0.2, 1) 0s 1 normal none running modal-slide-in',
	}),
	modalHeader: css(su.text2xl, {
		color: theme.colors.text.primary,
		flex: '1 1 auto',
		margin: '0px',
		marginBottom: '1.25rem',
		fontWeight: '700',
	}),
	modalBody: css({ flex: '1 1 auto', marginBottom: '0.75rem', margin: '0px' }),
	modalFooter: css({
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		flex: '1 1 auto',
		margin: '0px',
		marginTop: '1.25rem',
		'& > :not(:first-of-type)': css({ marginLeft: '1.25rem' }),
	}),
	backdrop: css({
		zIndex: -1,
		position: 'fixed',
		inset: 0,
		background: '#00000033',
		WebkitTapHighlightColor: 'transparent',
	}),
};

export const Backdrop = prepareForSlot(
	forwardRef<HTMLDivElement, { open?: boolean; className: string }>(
		function BackdropRender(props, ref) {
			return <div {...props} ref={ref} css={styles.backdrop} />;
		}
	)
);

export interface ModalProps {
	show: boolean;
	children: ReactNode;
	closeOnOverlayClick?: boolean;
	closeOnEscapePress?: boolean;
	disableFirstElementFocus?: boolean;
	testid?: string;
	dialogStyleOverrides?: CSSInterpolation;
	rootStyleOverrides?: CSSInterpolation;
	onRequestClose?: () => void;
}

export function Modal(props: ModalProps) {
	const {
		show,
		children,
		closeOnOverlayClick,
		closeOnEscapePress,
		disableFirstElementFocus,
		testid = 'modal',
		dialogStyleOverrides,
		rootStyleOverrides,
		onRequestClose,
	} = props;

	const root = useMemo(
		() =>
			prepareForSlot(
				forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(
					function Root(rootProps, ref) {
						return <div {...rootProps} ref={ref} css={rootStyleOverrides} />;
					}
				)
			),
		[rootStyleOverrides]
	);

	const windowHeight = useWindowViewHeight();

	return (
		<MuiModal
			open={show}
			css={styles.modal}
			onClose={(_e, reason) => {
				if (reason === 'backdropClick' && closeOnOverlayClick) {
					onRequestClose?.();
				} else if (reason === 'escapeKeyDown' && closeOnEscapePress) {
					onRequestClose?.();
				}
			}}
			slots={{
				backdrop: Backdrop,
				root,
			}}
			disableAutoFocus={disableFirstElementFocus}
		>
			<div
				data-testid={testid}
				css={[
					styles.modalDialogOverride,
					{ maxHeight: windowHeight - 24 },
					dialogStyleOverrides,
				]}
			>
				{children}
			</div>
		</MuiModal>
	);
}

interface ModalDisplayProps {
	children?: ReactNode;
	containerStyle?: CSSInterpolation;
	className?: string;
}

function ModalHeader(props: ModalDisplayProps) {
	return (
		<div
			css={[styles.modalHeader, props.containerStyle]}
			className={props.className}
		>
			{props.children}
		</div>
	);
}

function ModalFooter(props: ModalDisplayProps) {
	return (
		<div
			css={[styles.modalFooter, props.containerStyle]}
			className={props.className}
		>
			{props.children}
		</div>
	);
}

function ModalBody(props: ModalDisplayProps) {
	return (
		<div
			css={[styles.modalBody, props.containerStyle]}
			className={props.className}
		>
			{props.children}
		</div>
	);
}

/** @deprecated Use ModalWithLayout */
Modal.Header = ModalHeader;
/** @deprecated Use ModalWithLayout */
Modal.Footer = ModalFooter;
/** @deprecated Use ModalWithLayout */
Modal.Body = ModalBody;
